import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Switch } from "@loot-os/ui/components/switch";
import { useEffect, useState } from "react";

/**
 * The box's details tab, in either editor: the name, which is part of the draft and saved by the
 * header's Save, the slug, which is the box's storefront URL and never changes after create, and
 * Featured. The images are on the Art tab.
 */
export function BoxMetadataCard({
  name,
  slug,
  goLiveAt,
  featured,
  onNameChange,
  onGoLiveAtChange,
  onFeaturedChange,
  pending,
  readOnly,
}: {
  name: string;
  slug: string;
  goLiveAt: Date | string | null;
  /**
   * Absent for a box template: featuring is per storefront, and a template row is one shared
   * record across every tenant that enabled it, so there is no per-tenant answer to store.
   */
  featured?: boolean;
  onNameChange: (value: string) => void;
  onGoLiveAtChange: (value: string | null) => void;
  onFeaturedChange?: (value: boolean) => void;
  pending: boolean;
  readOnly: boolean;
}) {
  const savedGoLiveAt = toLocalDateTime(goLiveAt);
  const [goLiveAtDraft, setGoLiveAtDraft] = useState(savedGoLiveAt);
  useEffect(() => setGoLiveAtDraft(savedGoLiveAt), [savedGoLiveAt]);
  const goLiveAtChanged = goLiveAtDraft !== savedGoLiveAt;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="meta-name">Name</Label>
            <Input
              id="meta-name"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              disabled={readOnly}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="meta-slug">Slug</Label>
            <Input id="meta-slug" type="text" value={slug} readOnly disabled />
            <p className="text-xs text-muted-foreground">
              The box's storefront URL, fixed once the box is created.
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          The name is saved with the header's Save, together with any other changes.
        </p>
        <div className="grid gap-2 border-t border-border pt-4">
          <Label htmlFor="meta-go-live-at">Go-live date</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id="meta-go-live-at"
              type="datetime-local"
              value={goLiveAtDraft}
              onChange={(event) => setGoLiveAtDraft(event.target.value)}
              disabled={pending || readOnly}
              className="sm:max-w-xs"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                onGoLiveAtChange(
                  goLiveAtDraft === "" ? null : new Date(goLiveAtDraft).toISOString(),
                )
              }
              disabled={pending || readOnly || !goLiveAtChanged}
            >
              {goLiveAtDraft === "" ? "Clear date" : "Save date"}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Shown in your local time. A pre-release box goes active on the next processing pass
            after this time, once its items and drop table pass the launch checks.
          </p>
        </div>
        {featured !== undefined && onFeaturedChange && (
          // Saves on toggle rather than waiting for Save: it is a switch, and a switch that
          // silently needs a second press to stick reads as broken.
          <div className="flex items-start gap-3 border-t border-border pt-4">
            <Switch
              id="meta-featured"
              checked={featured}
              onCheckedChange={onFeaturedChange}
              disabled={pending || readOnly}
            />
            <div className="grid gap-1">
              <Label htmlFor="meta-featured">Featured</Label>
              <p className="text-sm text-muted-foreground">
                Show this box in the storefront home page's featured row. With none featured, the
                row is hidden. Saves as soon as it is switched.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** A stored UTC instant as the browser's local datetime input value. */
function toLocalDateTime(value: Date | string | null): string {
  if (value === null) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
